import * as fs from 'fs';
import * as path from 'path';
import type { Survey, SurveyQuestion } from '../fetchSurvey/types/survey';
import type {
  LogicFilterGroup,
  LogicFilterLeaf,
  LogicFilterNode,
} from '../fetchSurvey/types/logic';
import { QUESTION_TYPE } from '../fetchSurvey/constants/questionTypes';
import {
  detectFramework,
  resolveSdkPackageName,
  type AgentFramework,
} from './detect-framework';
import { extractSurveyTypesFromSurvey } from './fetch-survey-inventory';

const PLACEHOLDER_RE = /\{\{([^}]+)\}\}/g;
const CUSTOM_FIELD_ID_RE = /_c_[a-f0-9]+/gi;

export interface FrameworkSection {
  framework: AgentFramework;
  packageName: string | undefined;
  frameworkLine: string;
  packageLine: string;
}

export interface ComponentInventoryRow {
  componentFile: string;
  requiredIf: string;
  blocking?: boolean;
}

export interface MatrixVariantRow {
  questionId: string;
  type: string;
  displayStyle: string;
  statementLayout: string;
  transposeTable: string;
  selectionMode: string;
}

export interface CustomFieldRow {
  id: string;
  usedIn: string;
}

export interface PlaceholderRow {
  token: string;
  key: string;
  questionIds: string;
}

export interface LogicInventoryRow {
  questionId: string;
  skipLogics: string;
  displayLogic: string;
  answerLogic: string;
  customFieldRefs: string;
}

function isLogicGroup(node: LogicFilterNode): node is LogicFilterGroup {
  return 'filters' in node && Array.isArray((node as LogicFilterGroup).filters);
}

function collectCustomFieldIdsFromLogic(nodes: LogicFilterNode[] | undefined, out: Set<string>): void {
  if (!nodes) return;
  for (const node of nodes) {
    if (isLogicGroup(node)) {
      collectCustomFieldIdsFromLogic(node.filters, out);
      continue;
    }
    const leaf = node as LogicFilterLeaf;
    const entity = leaf.details?.conditionEntity;
    if (entity?.entityType === 'CUSTOM_FIELD') {
      out.add(entity.id);
    }
  }
}

function collectCustomFieldIdsFromText(text: string | undefined, out: Set<string>): void {
  if (!text) return;
  const matches = text.match(CUSTOM_FIELD_ID_RE);
  if (matches) {
    for (const m of matches) out.add(m.toLowerCase() === m ? m : m);
  }
}

function hasAnswerLogicOnQuestion(question: SurveyQuestion): boolean {
  if (question.type === QUESTION_TYPE.MCQ || question.type === QUESTION_TYPE.RANK_ORDER) {
    if (question.options?.some(o => o.answerLogic)) return true;
  }
  if ('statementRows' in question && question.statementRows?.some(r => r.answerLogic)) return true;
  if ('scaleColumns' in question && question.scaleColumns?.some(c => c.answerLogic)) return true;
  return false;
}

function matrixDisplayStyle(question: SurveyQuestion): string {
  if ('displayStyle' in question && question.displayStyle) {
    return String(question.displayStyle);
  }
  if ('gridLayout' in question) {
    return question.gridLayout === 'dropdown' ? 'dropdown' : 'standard';
  }
  return '—';
}

const COMPONENT_BY_TYPE: Record<string, { file: string; label: string; blocking?: boolean }[]> = {
  [QUESTION_TYPE.CFM_MATRIX]: [{ file: 'LikertMatrixScale.tsx', label: 'CFM_MATRIX' }],
  [QUESTION_TYPE.CSAT_MATRIX]: [{ file: 'CsatMatrixScale.tsx', label: 'CSAT_MATRIX or RATING_MATRIX' }],
  [QUESTION_TYPE.RATING_MATRIX]: [{ file: 'CsatMatrixScale.tsx', label: 'CSAT_MATRIX or RATING_MATRIX' }],
  [QUESTION_TYPE.SLIDER_MATRIX]: [
    { file: 'SliderMatrixScale.tsx', label: 'SLIDER_MATRIX', blocking: true },
    { file: 'CustomSliderTrack.tsx', label: 'SLIDER_MATRIX or matrix graphics' },
  ],
  [QUESTION_TYPE.FILE_UPLOAD]: [
    { file: 'FileUploadScale.tsx', label: 'FILE_UPLOAD', blocking: true },
  ],
  [QUESTION_TYPE.NPS_SCALE]: [{ file: 'RatingScale.tsx', label: 'NPS_SCALE' }],
  [QUESTION_TYPE.HEATMAP]: [
    { file: 'HeatmapScale.tsx', label: 'HEATMAP' },
    { file: 'lib/surveyUi/heatmapCoords.ts', label: 'HEATMAP' },
  ],
  [QUESTION_TYPE.RANK_ORDER]: [{ file: 'RankOrderScale.tsx', label: 'RANK_ORDER' }],
};

export function surveyHasMcqOtherOption(survey: Survey): boolean {
  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (question.type !== QUESTION_TYPE.MCQ || !('options' in question)) continue;
      if (question.options?.some((o) => o.otherOption)) return true;
    }
  }
  return false;
}

const MCQ_OTHER_PICKER_CONTENT_TYPES = new Set(['PHONE_NUMBER', 'POSTAL_CODE', 'DATE']);

export function surveyHasMcqOtherPickerTypes(survey: Survey): boolean {
  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (question.type !== QUESTION_TYPE.MCQ || !('options' in question)) continue;
      for (const option of question.options ?? []) {
        const contentType = option.otherOption?.contentType;
        if (contentType && MCQ_OTHER_PICKER_CONTENT_TYPES.has(contentType)) return true;
      }
    }
  }
  return false;
}

export function extractFrameworkSection(projectRoot: string): FrameworkSection {
  const pkgPath = path.join(projectRoot, 'package.json');
  const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf8')) as {
    dependencies?: Record<string, string>;
    devDependencies?: Record<string, string>;
  };
  const framework = detectFramework(pkg);
  const packageName = resolveSdkPackageName(pkg);

  const checks: Record<AgentFramework, string> = {
    nextjs: '[x] Next.js App Router  [ ] Vite  [ ] CRA  [ ] Existing app',
    vite: '[ ] Next.js App Router  [x] Vite  [ ] CRA  [ ] Existing app',
    cra: '[ ] Next.js App Router  [ ] Vite  [x] CRA  [ ] Existing app',
    unknown: '[ ] Next.js App Router  [ ] Vite  [ ] CRA  [x] Existing app',
  };

  return {
    framework,
    packageName,
    frameworkLine: `- **Framework:** ${checks[framework]}`,
    packageLine: `- **Package import name:** ${packageName ?? '(add @explorer02/cfm-survey-sdk or @repo/sdk)'}`,
  };
}

export function extractComponentInventory(survey: Survey): ComponentInventoryRow[] {
  const types = new Set(extractSurveyTypesFromSurvey(survey));
  const rows: ComponentInventoryRow[] = [
    { componentFile: 'IntroPage.tsx', requiredIf: 'intro page' },
    { componentFile: 'EndPage.tsx', requiredIf: 'end pages' },
    { componentFile: 'PausedPage.tsx', requiredIf: survey.pausedPage ? 'paused flag' : 'paused flag (if enabled)' },
  ];

  const seen = new Set<string>();
  for (const type of types) {
    const entries = COMPONENT_BY_TYPE[type];
    if (!entries) continue;
    for (const entry of entries) {
      const key = `${entry.file}:${entry.label}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        componentFile: entry.file,
        requiredIf: entry.label,
        blocking: entry.blocking,
      });
    }
  }

  if (types.has(QUESTION_TYPE.NPS_SCALE) || [...types].some((t) => t.includes('MATRIX'))) {
    rows.push({
      componentFile: 'lib/surveyUi/scaleValues.ts + getAccentColor.ts',
      requiredIf: 'NPS/matrix colors',
    });
  }
  if (types.has(QUESTION_TYPE.CSAT_MATRIX) || types.has(QUESTION_TYPE.RATING_MATRIX)) {
    rows.push({ componentFile: 'lib/surveyUi/icons.tsx', requiredIf: 'CSAT emoji/star' });
  }

  const hasDropdownMatrix = survey.pages.some((p) =>
    p.questions.some(
      (q) => 'gridLayout' in q && q.gridLayout === 'dropdown',
    ),
  );
  if (hasDropdownMatrix) {
    rows.push({ componentFile: 'MatrixDropdown.tsx', requiredIf: 'matrix dropdown/selectbox' });
  }

  return rows;
}

export function extractMatrixVariants(survey: Survey): MatrixVariantRow[] {
  const rows: MatrixVariantRow[] = [];
  const matrixTypes = [
    QUESTION_TYPE.CFM_MATRIX,
    QUESTION_TYPE.CSAT_MATRIX,
    QUESTION_TYPE.RATING_MATRIX,
    QUESTION_TYPE.SLIDER_MATRIX,
  ] as const;

  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (!matrixTypes.includes(question.type as (typeof matrixTypes)[number])) continue;
      rows.push({
        questionId: question.id,
        type: question.type,
        displayStyle: matrixDisplayStyle(question),
        statementLayout: 'statementLayout' in question ? String(question.statementLayout) : '—',
        transposeTable: 'transposeTable' in question ? String(question.transposeTable) : '—',
        selectionMode: 'selectionMode' in question ? String(question.selectionMode) : '—',
      });
    }
  }
  return rows;
}

export function extractCustomFieldInventory(survey: Survey): CustomFieldRow[] {
  const idToUses = new Map<string, Set<string>>();

  const add = (id: string, use: string) => {
    const normalized = id.startsWith('_c_') ? id : `_c_${id.replace(/^_c_/, '')}`;
    const set = idToUses.get(normalized) ?? new Set<string>();
    set.add(use);
    idToUses.set(normalized, set);
  };

  const scanLogic = (nodes: LogicFilterNode[] | undefined, use: string) => {
    const ids = new Set<string>();
    collectCustomFieldIdsFromLogic(nodes, ids);
    for (const id of ids) add(id, use);
  };

  for (const page of survey.pages) {
    for (const question of page.questions) {
      if (question.skipLogics?.length) {
        for (const sl of question.skipLogics) scanLogic(sl.condition.filters, 'skip');
      }
      if (question.displayLogic) scanLogic(question.displayLogic.condition.filters, 'display');
      if (question.displayVariants?.length) {
        for (const v of question.displayVariants) {
          if (v.displayLogic) scanLogic(v.displayLogic.condition.filters, 'display-variant');
          collectCustomFieldIdsFromText(v.questionText, new Set());
          const textIds = new Set<string>();
          collectCustomFieldIdsFromText(v.questionText, textIds);
          for (const id of textIds) add(id, 'copy');
        }
      }
      collectCustomFieldIdsFromText(question.questionText, new Set());
      const copyIds = new Set<string>();
      collectCustomFieldIdsFromText(question.questionText, copyIds);
      collectCustomFieldIdsFromText(question.questionDescription, copyIds);
      for (const id of copyIds) add(id, 'copy');

      if (question.type === QUESTION_TYPE.MCQ || question.type === QUESTION_TYPE.RANK_ORDER) {
        for (const opt of question.options) {
          if (opt.answerLogic) scanLogic(opt.answerLogic.condition.filters, 'answer');
        }
      }
      if ('statementRows' in question) {
        for (const row of question.statementRows) {
          if (row.answerLogic) scanLogic(row.answerLogic.condition.filters, 'answer');
        }
      }
      if ('scaleColumns' in question) {
        for (const col of question.scaleColumns) {
          if (col.answerLogic) scanLogic(col.answerLogic.condition.filters, 'answer');
        }
      }
    }
  }

  for (const endPage of survey.endPages) {
    if (endPage.displayLogic) scanLogic(endPage.displayLogic.condition.filters, 'end');
    const copyIds = new Set<string>();
    collectCustomFieldIdsFromText(endPage.header, copyIds);
    collectCustomFieldIdsFromText(endPage.description, copyIds);
    for (const id of copyIds) add(id, 'copy');
  }

  return [...idToUses.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, uses]) => ({ id, usedIn: [...uses].sort().join(', ') }));
}

export function extractLogicInventory(survey: Survey): LogicInventoryRow[] {
  const rows: LogicInventoryRow[] = [];

  for (const page of survey.pages) {
    for (const question of page.questions) {
      const cfIds = new Set<string>();
      if (question.skipLogics) {
        for (const sl of question.skipLogics) collectCustomFieldIdsFromLogic(sl.condition.filters, cfIds);
      }
      if (question.displayLogic) {
        collectCustomFieldIdsFromLogic(question.displayLogic.condition.filters, cfIds);
      }
      if (question.type === QUESTION_TYPE.MCQ || question.type === QUESTION_TYPE.RANK_ORDER) {
        for (const opt of question.options) {
          if (opt.answerLogic) collectCustomFieldIdsFromLogic(opt.answerLogic.condition.filters, cfIds);
        }
      }
      if ('statementRows' in question) {
        for (const row of question.statementRows) {
          if (row.answerLogic) collectCustomFieldIdsFromLogic(row.answerLogic.condition.filters, cfIds);
        }
      }
      if ('scaleColumns' in question) {
        for (const col of question.scaleColumns) {
          if (col.answerLogic) collectCustomFieldIdsFromLogic(col.answerLogic.condition.filters, cfIds);
        }
      }

      const hasSkip = Boolean(question.skipLogics?.length);
      const hasDisplay = Boolean(question.displayLogic);
      const hasAnswer = hasAnswerLogicOnQuestion(question);

      if (!hasSkip && !hasDisplay && !hasAnswer && cfIds.size === 0) continue;

      rows.push({
        questionId: question.id,
        skipLogics: hasSkip ? 'yes' : '—',
        displayLogic: hasDisplay ? 'yes' : '—',
        answerLogic: hasAnswer ? 'yes' : '—',
        customFieldRefs: cfIds.size ? [...cfIds].sort().join(', ') : '—',
      });
    }
  }

  return rows;
}

export function extractPlaceholderTokens(survey: Survey): PlaceholderRow[] {
  const tokenToQuestions = new Map<string, Set<string>>();

  const scanText = (text: string | undefined, questionId: string) => {
    if (!text) return;
    let match: RegExpExecArray | null;
    PLACEHOLDER_RE.lastIndex = 0;
    while ((match = PLACEHOLDER_RE.exec(text)) !== null) {
      const raw = match[1]?.trim();
      if (!raw) continue;
      const token = `{{${raw}}}`;
      const set = tokenToQuestions.get(token) ?? new Set<string>();
      set.add(questionId);
      tokenToQuestions.set(token, set);
    }
  };

  for (const page of survey.pages) {
    for (const question of page.questions) {
      scanText(question.questionText, question.id);
      scanText(question.questionDescription, question.id);
      if (question.displayVariants) {
        for (const v of question.displayVariants) scanText(v.questionText, question.id);
      }
    }
  }

  return [...tokenToQuestions.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([token, qids]) => {
      const key = token.startsWith('{{_c_')
        ? `CUSTOM_FIELD_VALUES['${token.slice(2, -2)}']`
        : token.startsWith('{{SURVEY_QUESTION')
          ? '(resolved in hook — render question.questionText as-is)'
          : `SURVEY_PLACEHOLDERS.${token.slice(2, -2)}`;
      return {
        token,
        key,
        questionIds: [...qids].sort().join(', '),
      };
    });
}

export function formatComponentTableRows(rows: ComponentInventoryRow[]): string {
  return rows
    .map((r) => {
      const block = r.blocking ? ' **blocking**' : '';
      return `| \`${r.componentFile}\` | ${r.requiredIf}${block} | |`;
    })
    .join('\n');
}

export function formatMatrixTableRows(rows: MatrixVariantRow[]): string {
  if (!rows.length) return '| (no matrix questions in fetch) | | | | | | | |';
  return rows
    .map(
      (r) =>
        `| ${r.questionId} | ${r.type} | ${r.displayStyle} | ${r.statementLayout} | ${r.transposeTable} | ${r.selectionMode} | |`,
    )
    .join('\n');
}

export function formatCustomFieldTableRows(rows: CustomFieldRow[]): string {
  if (!rows.length) return '| (no _c_ ids in fetch) | | | | | |';
  return rows
    .map((r) => `| ${r.id} | | ${r.usedIn} | | | |`)
    .join('\n');
}

export function formatPlaceholderTableRows(rows: PlaceholderRow[]): string {
  if (!rows.length) return '| (no {{TOKEN}} in fetch) | | | |';
  return rows.map((r) => `| ${r.token} | ${r.key} | | ${r.questionIds} |`).join('\n');
}

export function formatLogicTableRows(rows: LogicInventoryRow[]): string {
  if (!rows.length) return '| (no logic flags in fetch) | | | | |';
  return rows
    .map(
      (r) =>
        `| ${r.questionId} | ${r.skipLogics} | ${r.displayLogic} | ${r.answerLogic} | ${r.customFieldRefs} |`,
    )
    .join('\n');
}
